import { ref, watch, shallowRef, nextTick, getCurrentInstance } from 'vue'
import * as PDFJS from 'pdfjs-dist'
import { getDomain, getSrc, get } from '../../../utils/index'
import { ifPdf } from '../util/file-util'
import { usePdfImg } from './use-pdf-img'

PDFJS.GlobalWorkerOptions.workerSrc = getSrc('/static/pdf/2.6.347/build/pdf.worker.min.js')
export function usePdf({
  attach,
  boxRatio,
  pdfReplaceUrl = '/resources/',
  transformPdfImg,
  showPdfInIframe,
}) {
  const canvasRef = ref(null)

  //获取PDF数据
  const loadFile = (url) => {
    return new Promise(async (resolve) => {
      // 此处如果是http地址则转换为当前域名下的地址，由后台去做反代
      const _url = pdfReplaceUrl ? url?.replace?.(/^https?:\/\/.*.com\//, pdfReplaceUrl) : url
      const [res] = await get(getDomain(_url), {}, { responseType: 'blob' }, (resData) => ({
        code: 200,
        data: resData,
      })).catch(() => [])
      if (res) {
        const reader = new FileReader()
        reader.onload = async (_res) => {
          if (_res.target.result) {
            const pdfDocRes = await __readPdf(_res.target.result)
            resolve(pdfDocRes)
          } else {
            console.log('加载 PDF 数据失败！')
            resolve()
          }
        }
        reader.readAsArrayBuffer(res.data)
      } else {
        resolve()
      }
    })
  }

  //读取PDF数据
  const __readPdf = (data) => {
    return new Promise((resolv) => {
      const pdfInstance = PDFJS.getDocument({
        data: data,
        cMapUrl: getSrc('/static/pdf/2.6.347/cmaps/'),
        cMapPacked: true,
      })
      // data 是一个 ArrayBuffer 格式，也是一个 buffer 流的数据
      pdfInstance.promise
        .then((pdfDocRes) => {
          resolv(pdfDocRes)
        })
        .catch((error) => {
          resolv()
          console.log('error: ', error)
        })
    })
  }

  //渲染pdf
  const renderPdf = async (curPage) => {
    if (!pdfDoc.value) {
      console.warn('pdf.doc 不可为空！(列表模式下可能会多出现一次本警告可忽略它)')
      return
    }
    if (!curPage) throw new Error('curPage 不可为 0 或空！')

    const page = await pdfDoc.value.getPage(curPage)
    if (!canvasRef.value) throw new Error('未获取到 Canvas !')
    // 设置 canvas 相关的属性
    const ctx = canvasRef.value.getContext('2d')
    const dpr = window.devicePixelRatio || 1 // 设备像素比
    const bsr =
      ctx.webkitBackingStorePixelRatio ||
      ctx.mozBackingStorePixelRatio ||
      ctx.msBackingStorePixelRatio ||
      ctx.oBackingStorePixelRatio ||
      ctx.backingStorePixelRatio ||
      1 // 浏览器在渲染 canvas 之前会用几个像素来来存储画布信息,类似img
    const ratio = dpr / bsr
    const viewport = page.getViewport({ scale: boxRatio() })

    canvasRef.value.width = viewport.width * ratio
    canvasRef.value.height = viewport.height * ratio
    // canvasRef.value.style.width = viewport.width + 'px'
    // canvasRef.value.style.height = viewport.height + 'px'

    const renderContext = {
      canvasContext: ctx,
      viewport,
      // 保证根据手势缩放后的清晰度和等比例
      transform: [ratio, 0, 0, ratio, 0, 0],
    }

    // 渲染
    page.render(renderContext)
  }

  const { proxy } = getCurrentInstance()
  const transformPdfIframeUrl = (url) => {
    if (!url) return { url: '' }
    url = url.replace(/^https?:\/\/[^\/]+\//, '/')
    if (!url.startsWith('/pangu/')) {
      url = '/pangu_/' + url.replace(/^\//, '')
    } else if (url.startsWith('/pangu/')) {
      url = url.replace(/^\/pangu\//, '/pangu_/')
    }

    const satrtUrl = proxy.GLOBAL.baseURL.replace(/\/[^\/]+\/$/, '')
    return {
      url: `${satrtUrl}${url}`,
    }
  }

  const pageNo = ref(1)
  const pageTotal = ref(0)
  const pdfDoc = shallowRef(null)

  const iframeAttach = ref(null)
  const { imgAttach, transfromImg } = usePdfImg({ attach, pageNo })
  watch(
    attach,
    async () => {
      if (attach.value?.url && ifPdf(attach.value)) {
        if (!transformPdfImg && !showPdfInIframe) {
          pdfDoc.value = await loadFile(attach.value.url)
          nextTick(() => {
            pageTotal.value = pdfDoc.value.numPages
            pageNo.value = 1
            renderPdf(pageNo.value)
          })
        } else if (transformPdfImg && !showPdfInIframe) {
          const imgList = await transfromImg()
          pageTotal.value = imgList.length
          pageNo.value = 1
        } else {
          iframeAttach.value = transformPdfIframeUrl(attach.value.url)
        }
      }
    },
    { immediate: true }
  )

  const nextPage = () => {
    if (ifPdf(attach.value) && pageNo.value < pageTotal.value) {
      pageNo.value += 1
      if (!transformPdfImg && !showPdfInIframe) renderPdf(pageNo.value)
    }
  }
  const lastPage = () => {
    if (ifPdf(attach.value) && pageNo.value > 1) {
      pageNo.value -= 1
      if (!transformPdfImg && !showPdfInIframe) renderPdf(pageNo.value)
    }
  }

  return {
    canvasRef,
    nextPage,
    lastPage,
    pageNo,
    pageTotal,
    imgAttach,
    iframeAttach,
  }
}
